import { useMemo } from "react";

import { type GameTypeConfig, type TagRowData } from "./game-type-configs";
import { getRowPitchResult } from "./notAPitch";

type UseTransformedTagsDataParams = {
  tags: unknown[] | undefined;
  game: unknown;
  config: GameTypeConfig;
};

type UseTransformedTagsDataReturn = {
  tagsData: TagRowData[];
  availableInnings: Array<{ value: string; label: string; id: string }>;
};

export const useTransformedTagsData = ({
  tags,
  game,
  config,
}: UseTransformedTagsDataParams): UseTransformedTagsDataReturn => {
  return useMemo(() => {
    if (!tags || tags.length === 0 || !game) {
      return {
        tagsData: [],
        availableInnings: [{ value: "all", label: "All Innings", id: "all" }],
      };
    }

    const inningsSet = new Set<number>();

    const transformedData: TagRowData[] = tags.map((tag, index) => {
      const tagData = (tag as { tag_data?: unknown }).tag_data as
        | {
            gameState?: {
              inning?: { integer?: number; half?: "top" | "bottom" };
              currentPitcher?: { playerName?: string; externalId?: string };
              currentHitter?: { playerName?: string; externalId?: string };
            };
            metadata?: {
              event_type?: string;
              pitchType?: string;
              pitchResult?: string;
              playResult?: string;
            };
          }
        | undefined;

      const gameState = tagData?.gameState;
      const metadata = tagData?.metadata;

      const pitchData = (tag as { pitch_data?: unknown }).pitch_data as
        | { RelSpeedMPH?: number; SpinRateRPM?: number }
        | undefined;

      const hitData = (tag as { hit_data?: unknown }).hit_data as
        | {
            ExitSpeedMPH?: number;
            AngleDegrees?: number;
            DistanceFeet?: number;
          }
        | undefined;

      const row: TagRowData = {
        eventUuid: (tag as { event_uuid: string }).event_uuid,
        pitchNumber: index + 1,
      };

      const extractField = (key: keyof TagRowData) => {
        switch (key) {
          case "inning":
            row.inning = gameState?.inning?.integer || 0;
            break;
          case "half":
            row.half = gameState?.inning?.half || "top";
            break;
          case "pitcher":
            row.pitcher = gameState?.currentPitcher?.playerName || "Unknown";
            row.pitcherExternalId = gameState?.currentPitcher?.externalId;
            Object.assign(row, {
              pitcherName: gameState?.currentPitcher?.playerName,
            });
            break;
          case "hitter":
            row.hitter = gameState?.currentHitter?.playerName || "Unknown";
            row.hitterExternalId = gameState?.currentHitter?.externalId;
            Object.assign(row, {
              hitterName: gameState?.currentHitter?.playerName,
            });
            break;
          case "pitchType":
            row.pitchType = metadata?.pitchType || "Unknown";
            break;
          case "velocity":
            row.velocity = pitchData?.RelSpeedMPH ?? null;
            break;
          case "spinRate":
            row.spinRate = pitchData?.SpinRateRPM ?? null;
            break;
          case "exitVelo":
            row.exitVelo = hitData?.ExitSpeedMPH ?? null;
            break;
          case "launchAngle":
            row.launchAngle = hitData?.AngleDegrees ?? null;
            break;
          case "distance":
            row.distance = hitData?.DistanceFeet ?? null;
            break;
          case "pitchResult":
            row.pitchResult = getRowPitchResult(metadata);
            break;
          case "playResult":
            row.playResult = metadata?.playResult || "Unknown";
            break;
        }
      };

      if (config.needsData) {
        config.needsData.forEach(extractField);
      }

      config.columns.forEach((col) => extractField(col.key));

      if (row.inning && row.inning > 0) {
        inningsSet.add(row.inning);
      }

      return row;
    });

    // Build innings options from collected data
    const sortedInnings = Array.from(inningsSet).sort((a, b) => a - b);
    const innings = [
      { value: "all", label: "All Innings", id: "all" },
      ...sortedInnings.map((inning) => ({
        value: String(inning),
        label: `Inning ${inning}`,
        id: String(inning),
      })),
    ];

    return {
      tagsData: transformedData,
      availableInnings: innings,
    };
  }, [tags, game, config.columns, config.needsData]);
};
